import { useState, useEffect, useCallback } from 'react';
import { localStorageUtil } from '../utils/localStorage';

/**
 * Custom hook for managing localStorage with type safety and error handling
 * Automatically falls back to in-memory storage when localStorage is unavailable (private mode, etc.)
 * @param key - The localStorage key
 * @param initialValue - The initial value if no stored value exists
 * @returns [value, setValue, removeValue] tuple
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
  // State to store our value
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      // Get from local storage (or fallback) by key
      const item = localStorageUtil.getItem<T>(key);
      // Return stored value or initialValue if none exists
      return item !== null ? item : initialValue;
    } catch (error) {
      // If error also return initialValue
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that persists the new value to localStorage
  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStoredValue(prevValue => {
        // Functional update keeps the latest value without stale closures
        const valueToStore = value instanceof Function ? value(prevValue) : value;

        try {
          // Save to local storage (or fallback)
          localStorageUtil.setItem(key, valueToStore);
        } catch (error) {
          // Handle QuotaExceededError gracefully
          if (error instanceof Error && error.name === 'QuotaExceededError') {
            console.warn(`localStorage quota exceeded for key "${key}". Consider clearing old data.`);
          } else {
            console.error(`Error setting localStorage key "${key}":`, error);
          }
        }

        return valueToStore;
      });
    },
    [key]
  );

  // Function to remove the value from localStorage (or fallback)
  const removeValue = useCallback(() => {
    try {
      setStoredValue(initialValue);
      localStorageUtil.removeItem(key);
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error);
    }
  }, [key, initialValue]);

  // Listen for changes to localStorage from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setStoredValue(JSON.parse(e.newValue));
        } catch (error) {
          console.warn(`Error parsing localStorage value for key "${key}":`, error);
        }
      } else if (e.key === key && e.newValue === null) {
        // Key was removed
        setStoredValue(initialValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, initialValue]); // Keep initialValue dependency for correctness

  return [storedValue, setValue, removeValue];
}

export default useLocalStorage;
